import { useState } from 'react'
import { View, Text, Pressable, StyleSheet } from 'react-native'
import { useTranslation } from 'react-i18next'
import Ionicons from '@expo/vector-icons/Ionicons'
import { fonts } from '../../lib/theme'
import { useThemeColors } from '../../lib/useThemeColors'
import { useAvatars } from '../../lib/useAvatars'
import { RANK_COLORS } from '../../lib/rankColors'
import { Podio } from '../Leaderboard/Classifica'
import PersonAvatarFace from '../PersonAvatarFace'
import RankBadge from '../RankBadge'
import { Card, SectionLabel } from './ui'

const VISIBLE_ROWS = 7   // dal 4° al 10°; gli altri dietro "Mostra tutti". La riga dell'utente resta sempre in vista.

function Row({ u, isMe, avatars }) {
  const { t } = useTranslation()
  const tc = useThemeColors()
  return (
    <View
      accessible
      accessibilityLabel={`${u.posizione}, ${u.out_display_name}, ${u.punti_ranking} pt, ${t('leagues.races_count', { count: u.gare_disputate })}`}
      style={[s.row, { backgroundColor: isMe ? tc.rosaSubtle : tc.bgCard2, borderColor: isMe ? tc.rosaBorder : 'transparent' }]}
    >
      <RankBadge pos={u.posizione} size={16} />
      <PersonAvatarFace userId={u.out_id_user} settings={avatars[u.out_id_user]} size={34} ring={RANK_COLORS[u.posizione]} />
      <Text style={[s.name, { color: isMe ? tc.rosaText : tc.text, fontWeight: isMe ? '700' : '500' }]} numberOfLines={1}>{u.out_display_name}</Text>
      <Text style={[s.races, { color: tc.textMuted }]}>{t('leagues.races_count', { count: u.gare_disputate })}</Text>
      <Text style={[s.pts, { color: tc.rosaText }]}>{u.punti_ranking} pt</Text>
    </View>
  )
}

export default function ClassificaStagionale({ stagionale, currentUserId }) {
  const { t } = useTranslation()
  const tc = useThemeColors()
  const [showAll, setShowAll] = useState(false)
  const avatars = useAvatars(stagionale.map(u => u.out_id_user))

  const rest = stagionale.slice(3)
  const head = rest.slice(0, VISIBLE_ROWS)
  const tail = rest.slice(VISIBLE_ROWS)
  const me = tail.find(u => u.out_id_user === currentUserId)   // sono oltre il 10°: la mia riga resta in vista

  return (
    <Card>
      <View style={{ marginBottom: 16 }}>
        <SectionLabel>{t('leagues.stagionale')}</SectionLabel>
      </View>

      {stagionale.length > 0 && (
        <Podio
          data={stagionale}
          nameKey="out_display_name"
          valueKey="punti_ranking"
          valueLabel="pt"
          currentUserId={currentUserId}
          userIdKey="out_id_user"
          faces
        />
      )}

      {rest.length > 0 && (
        <>
          <View style={{ gap: 4, marginBottom: 10 }}>
            {head.map(u => <Row key={u.out_id_user} u={u} isMe={u.out_id_user === currentUserId} avatars={avatars} />)}
            {showAll && tail.map(u => <Row key={u.out_id_user} u={u} isMe={u.out_id_user === currentUserId} avatars={avatars} />)}
            {!showAll && me && (
              <>
                <Text style={[s.gap, { color: tc.textDim }]} accessibilityElementsHidden importantForAccessibility="no">• • •</Text>
                <Row u={me} isMe avatars={avatars} />
              </>
            )}
          </View>
          {tail.length > 0 && (
            <Pressable
              onPress={() => setShowAll(v => !v)}
              accessibilityRole="button"
              accessibilityState={{ expanded: showAll }}
              style={({ pressed }) => [s.showAll, { borderColor: tc.border }, pressed && { opacity: 0.7 }]}
            >
              <Text style={[s.showAllText, { color: tc.textMuted }]}>
                {showAll ? t('leagues.hide') : t('leagues.show_all', { count: tail.length })}
              </Text>
              <Ionicons name={showAll ? 'chevron-up' : 'chevron-down'} size={14} color={tc.textMuted} />
            </Pressable>
          )}
        </>
      )}

      {stagionale.length === 0 && (
        <Text style={[s.noData, { color: tc.textMuted }]}>{t('leagues.no_data')}</Text>
      )}
    </Card>
  )
}

const s = StyleSheet.create({
  row: { flexDirection: 'row', alignItems: 'center', gap: 10, padding: 8, paddingHorizontal: 12, borderRadius: 10, borderWidth: 1, minHeight: 48 },
  name: { flex: 1, fontSize: 14 },
  races: { fontSize: 12 },
  pts: { fontSize: 14, fontFamily: fonts.condensedBold, minWidth: 48, textAlign: 'right' },
  gap: { textAlign: 'center', fontSize: 12, letterSpacing: 4 },
  showAll: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6, minHeight: 44, borderWidth: 1, borderRadius: 10 },
  showAllText: { fontFamily: fonts.condensedBold, fontSize: 13 },
  noData: { fontSize: 13, textAlign: 'center' },
})
